import { FlexRow } from '@/components/common/Flex';
import Modal from '@/components/common/modals/Modal';
import { useToasts } from '@/components/common/Toasts';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import { ApolloError, useMutation } from '@apollo/client';
import { VStack } from '@chakra-ui/react';
import { AnyDepositoryAccount } from '@common/financialEntities';
import {
    AllFinancialEntitiesDocument,
    CloseAtomicAccountDocument,
} from '@common/graphql/generated';
import { TOASTS } from '@common/toasts';
import { useState } from 'react';
import Balancer from 'react-wrap-balancer';
import Banner from '../common/Banner';
import { Button } from '../common/Button';
import FadeInBox from '../common/FadeInBox';
import { FlexCol } from '../common/Flex';
import { InfoCircle } from '../common/icons';
import { Header, LinkText, Paragraph } from '../common/Typography';
import { DepositoryAccountItem } from '../financialEntities/DepositoryAccountSelectionView';

export interface ConfirmCloseAtomicAccountModalProps {
    atomicAccountId: string;
    hasBalance: boolean;
    selectableDepositoryAccounts: AnyDepositoryAccount[];
    accountType: 'individual' | 'joint';
}

export const showConfirmCloseAtomicAccountModal = (
    props: ConfirmCloseAtomicAccountModalProps
) => ModalManager.show(ConfirmCloseAtomicAccountModal, props);

const ConfirmCloseAtomicAccountModal =
    ModalManager.create<ConfirmCloseAtomicAccountModalProps>(
        ({
            atomicAccountId,
            selectableDepositoryAccounts,
            hasBalance,
            accountType,
        }) => {
            useTrackModalShown('Confirm Close Atomic Account');

            const modal = useModal();
            const [depositoryAccountId, setDepositoryAccountId] =
                useState<string>();
            const [closeAtomicAccount, { loading: isSubmitting }] = useMutation(
                CloseAtomicAccountDocument,
                { refetchQueries: [AllFinancialEntitiesDocument] }
            );
            const { showSuccessToast, showErrorToast } = useToasts();

            const submit = async () => {
                if ((hasBalance && !depositoryAccountId) || isSubmitting) {
                    return;
                }

                try {
                    await closeAtomicAccount({
                        variables: {
                            atomicAccountId,
                            depositoryAccountId,
                        },
                    });

                    let successMessage = `We’ll begin closing your ${accountType} account.`;
                    if (hasBalance) {
                        successMessage +=
                            ' Funds will be transferred in the next 5 - 7 days.';
                    }
                    showSuccessToast(successMessage);
                    modal.remove();
                } catch (e) {
                    if (
                        e instanceof ApolloError &&
                        e.graphQLErrors.length > 0 &&
                        (e.graphQLErrors[0].extensions?.error_type ===
                            'ITEM_LOGIN_REQUIRED' ||
                            e.graphQLErrors[0].extensions?.code?.includes(
                                'link_error'
                            ))
                    ) {
                        showErrorToast(
                            "We've lost connection to this bank account. Please relink it to continue with closing your account."
                        );
                    } else {
                        showErrorToast(TOASTS.unexpectedErrorPleaseReachOut);
                    }

                    logError(e);
                }
            };

            return (
                <Modal
                    size="xl"
                    isOpen={modal.visible}
                    onClose={modal.remove}
                    renderHeader={() => (
                        <Balancer>
                            <Header>
                                {hasBalance
                                    ? 'Choose a bank account to withdraw to'
                                    : 'Confirm account closure'}
                            </Header>
                        </Balancer>
                    )}
                    renderFooter={() => (
                        <FlexRow justifyContent="flex-end" gap={4} p={4}>
                            <Button
                                disabled={hasBalance && !depositoryAccountId}
                                isLoading={isSubmitting}
                                onClick={submit}
                                onClickEvent="Confirm Close Atomic Account Submit Button Clicked"
                            >
                                {hasBalance
                                    ? 'Withdraw funds & close account'
                                    : 'Close account'}
                            </Button>
                        </FlexRow>
                    )}
                >
                    {hasBalance ? (
                        <DepositoryAccountStepContent
                            depositoryAccountId={depositoryAccountId}
                            setDepositoryAccountId={setDepositoryAccountId}
                            accounts={selectableDepositoryAccounts}
                        />
                    ) : (
                        <Paragraph>
                            Please confirm that you would like to close your
                            account.
                        </Paragraph>
                    )}
                </Modal>
            );
        }
    );

const DepositoryAccountStepContent = ({
    depositoryAccountId,
    setDepositoryAccountId,
    accounts,
}: {
    depositoryAccountId: string | undefined;
    setDepositoryAccountId: (depositoryAccountId: string | undefined) => void;
    accounts: AnyDepositoryAccount[];
}) => {
    return (
        <FadeInBox>
            <FlexCol>
                {accounts.length === 0 ? (
                    <Banner icon={<InfoCircle />} mb={8}>
                        We need information about where to transfer remaining
                        funds before we can close your account. Please email our
                        support team at{' '}
                        <LinkText href="mailto:priority@withplenty.com">
                            priority@withplenty.com
                        </LinkText>
                        .
                    </Banner>
                ) : (
                    <>
                        <Paragraph mb={8}>
                            Please confirm the account you would like Plenty
                            funds withdrawn to.
                        </Paragraph>
                        <VStack spacing={4} align="stretch">
                            {accounts.map((a) => (
                                <DepositoryAccountItem
                                    key={a.id}
                                    account={a}
                                    isSelected={a.id === depositoryAccountId}
                                    onChange={setDepositoryAccountId}
                                />
                            ))}
                        </VStack>
                    </>
                )}
            </FlexCol>
        </FadeInBox>
    );
};

export default ConfirmCloseAtomicAccountModal;
